import { FloatingButton } from './components/floating-button';
import { ChatPanel } from './components/chat-panel';
import { FocusTrap } from './components/focus-trap';
import { ConversationManager } from './core/conversation-manager';
import { InputValidator } from './core/input-validator';
import { Llm } from './core/llm';
import { ChatbotController, LLMProvider, WidgetConfig } from './types';
import {
  DEFAULT_ARIA_LABEL,
  DEFAULT_TITLE,
  MAX_INPUT_LENGTH,
  RATE_LIMIT_COOLDOWN_MS,
  REQUEST_TIMEOUT_MS,
} from './constants';
import { CHATBOT_STYLES } from './styles/chatbot-styles';

/**
 * Injects the chatbot CSS styles into the document head.
 * This is done dynamically to avoid webpack module resolution issues.
 */
function injectChatbotStyles(): void {
  if (document.getElementById('chatbot-styles')) {
    return; // Already injected
  }

  const style = document.createElement('style');
  style.id = 'chatbot-styles';
  style.textContent = CHATBOT_STYLES;
  document.head.appendChild(style);
}

/**
 * Initializes the chatbot widget within the given container element.
 * Validates configuration, creates all internal components, wires event
 * handlers, and returns a controller for lifecycle management.
 */
export function init(config: WidgetConfig, container: HTMLElement): ChatbotController {
  // --- Validation ---
  if (!container || !(container instanceof HTMLElement)) {
    throw new Error('Invalid container: a valid DOM element is required.');
  }

  // --- Inject Styles ---
  injectChatbotStyles();

  // --- Root wrapper ---
  const root = document.createElement('div');
  root.className = 'chatbot-widget';
  root.setAttribute('data-chatbot-root', '');
  container.appendChild(root);

  // --- Provider ---
  const provider: LLMProvider = new Llm();

  // --- Input Validator ---
  const inputValidator = new InputValidator(MAX_INPUT_LENGTH, RATE_LIMIT_COOLDOWN_MS);

  // --- State ---
  let panelOpen = false;
  let conversationManager: ConversationManager | null = null;

  // --- Initialize Conversation Manager ---
  // Knowledge base is managed entirely by the backend API
  conversationManager = createConversationManager(provider);

  // --- UI Components ---
  const floatingButton = new FloatingButton(root, handleToggle);

  const chatPanel = new ChatPanel(root, {
    title: config.title ?? DEFAULT_TITLE,
    ariaLabel: config.ariaLabel ?? DEFAULT_ARIA_LABEL,
    onSubmit: handleSubmit,
    onClose: handleClose,
  });

  const focusTrap = new FocusTrap(chatPanel.getElement());

  // --- Event Handlers ---

  function handleToggle(): void {
    if (panelOpen) {
      closePanel();
    } else {
      openPanel();
    }
  }

  function openPanel(): void {
    panelOpen = true;
    floatingButton.setState(true);
    chatPanel.open();
    focusTrap.activate();
    chatPanel.showPlaceholder();
    chatPanel.focusInput();
  }

  function closePanel(): void {
    panelOpen = false;
    floatingButton.setState(false);
    chatPanel.close();
    focusTrap.deactivate();

    // Clear conversation state
    if (conversationManager) {
      conversationManager.clear();
    }
    chatPanel.clearMessages();

    // Return focus to FAB
    floatingButton.getElement().focus();
  }

  function handleClose(): void {
    closePanel();
  }

  async function handleSubmit(text: string): Promise<void> {
    if (!conversationManager) {
      chatPanel.showError('Chat service unavailable. Please try again.');
      return;
    }

    // Validate input
    const validation = inputValidator.validate(text);
    if (!validation.valid) {
      return;
    }

    // Check rate limit
    if (!inputValidator.canSubmit()) {
      return;
    }

    // Record submission for rate limiting
    inputValidator.recordSubmission();

    // Add user message and display it
    const userMessage = conversationManager.addUserMessage(text);
    chatPanel.appendMessage(userMessage);
    chatPanel.clearInput();
    chatPanel.setInputEnabled(false);
    chatPanel.showLoading();

    // Set up timeout
    const timeoutId = setTimeout(() => {
      if (conversationManager) {
        conversationManager.abort();
      }
      chatPanel.hideLoading();
      chatPanel.showError('Request timed out. Please try again.');
      chatPanel.setInputEnabled(true);
    }, REQUEST_TIMEOUT_MS);

    try {
      const assistantMessage = await conversationManager.sendToProvider();
      clearTimeout(timeoutId);
      chatPanel.hideLoading();
      chatPanel.appendMessage(assistantMessage);
      chatPanel.setInputEnabled(true);
    } catch (error) {
      clearTimeout(timeoutId);
      chatPanel.hideLoading();

      // Determine error message
      const errorMessage =
        error instanceof Error && error.name === 'AbortError'
          ? 'Request timed out. Please try again.'
          : 'Request failed. Please try again.';

      chatPanel.showError(errorMessage);
      chatPanel.setInputEnabled(true);
    }
  }

  // --- Destroy ---
  function destroy(): void {
    if (conversationManager) {
      conversationManager.abort();
    }
    focusTrap.destroy();
    chatPanel.destroy();
    floatingButton.destroy();
    root.remove();
  }

  return { destroy };
}

// --- Helpers ---

function createConversationManager(
  provider: LLMProvider,
): ConversationManager {
  return new ConversationManager({
    provider,
  });
}
